import styles from './index.module.css'
import { Theme } from '@radix-ui/themes'
import React, { useState } from 'react'
import { useSelector } from 'hooks/use-redux'
import { Button as RadixButton, Flex } from 'components/ds'
import HoverCard from 'components/ui/hover-card'
import cn from 'clsx'
type Props = {
  explain?: string
  explainValues?: Record<string, any>
  controls?: React.ReactNode
  children?: React.ReactNode
}

/**
 * A debug component that displays additional controls and information for staff users only
 * @param controls - Optional React node containing debug controls
 * @param explain - Optional debug explanation text
 * @param explainValues - Optional object containing debug values to display
 * @param children - The child components to render
 * @returns A staff-only debug interface with hover card containing controls and info
 */
const StaffOnly = ({
  controls,
  explain,
  explainValues,
  children,
}: Props): React.ReactNode => {
  const [state, setState] = useState<{ copied?: boolean }>({ copied: false })
  const isSupportUser = useSelector(state => state.user.is_support_user)
  if (!isSupportUser) return null

  if (parseInt(localStorage.getItem('staff_only_hide') ?? '') > +new Date()) {
    return null
  }

  const explainValuesFormatted = JSON.stringify(explainValues, null, '  ')
  return (
    <>
      {children}
      <HoverCard
        contentClassName={styles.StaffOnlyCard}
        content={
          <>
            <div className={styles.StaffOnlyBars}>
              {controls && (
                <Theme>
                  <div className={styles.StaffOnlyWrap}>{controls}</div>
                </Theme>
              )}
              {explain && <pre>{explain}</pre>}
              {explainValues && <pre>{explainValuesFormatted}</pre>}
              <Flex>
                <RadixButton
                  color="gray"
                  variant="solid"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      (explain || explainValuesFormatted || '') +
                        '\n' +
                        new Date()
                          .toISOString()
                          .slice(0, -5)
                          .replace('T', ' ') +
                        ' ' +
                        document.location.href,
                    )
                    setState({
                      copied: true,
                    })
                    setTimeout(
                      () =>
                        setState({
                          copied: false,
                        }),
                      2000,
                    )
                  }}
                >
                  <span>{state.copied ? 'copied' : 'copy'}</span>
                </RadixButton>
                <RadixButton
                  ml="auto"
                  color="orange"
                  variant="solid"
                  onClick={() => {
                    const until = (+new Date() + 5 * 60 * 1000).toString()
                    localStorage.setItem('staff_only_hide', until)
                    setState({})
                  }}
                >
                  hide for 5min
                </RadixButton>
              </Flex>
            </div>
          </>
        }
        activator={
          (controls || explain || explainValues) && (
            <div
              style={{
                position: 'relative',
                height: 0,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: -6,
                  left: 0,
                }}
              >
                <RadixButton
                  className={cn(styles.StaffOnly, styles.StaffOnlyBars)}
                  color="orange"
                  size="1"
                  variant="solid"
                >
                  STAFF ONLY
                </RadixButton>
              </div>
            </div>
          )
        }
      />
    </>
  )
}

export default StaffOnly
